import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, ChevronRight, ChevronLeft, CheckCircle2, Loader2,
  Clock, IndianRupee, MonitorSmartphone, GraduationCap, ArrowUpRight,
  Info, Award,
} from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { FormInput } from "@/components/shared/FormInput";
import { PrimaryButton, SecondaryButton } from "@/components/shared/Buttons";
import { useSeo } from "@/hooks/useSeo";
import { submitCourseRecommendation, QUALIFICATIONS, isValidIndianMobile, isValidEmail } from "@/lib/api";
import { detailPath, waLinkFor } from "@/lib/programsData";
import {
  recommendPrograms,
  TECH_LEVELS, CAREER_GOALS, DURATIONS, MODES, BUDGETS, INTEREST_TAGS,
} from "@/lib/recommendationEngine";

// ---------- Small UI helpers (kept local — one-shot page) ----------
const Select = ({ id, label, value, onChange, options, placeholder = "Select", error, testId }) => (
  <div className="flex flex-col gap-2">
    {label && (
      <label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-navy/70">
        {label}
      </label>
    )}
    <select
      id={id} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className={`w-full rounded-lg border px-4 py-3 text-sm outline-none transition-colors ${error ? "border-destructive" : "border-navy/15 focus:border-royal"} bg-white text-navy`}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (<option key={o} value={o}>{o}</option>))}
    </select>
    {error && <span className="text-xs text-destructive">{error}</span>}
  </div>
);

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
      active ? "border-orange bg-orange text-white" : "border-navy/15 bg-white text-navy/70 hover:border-orange"
    }`}
  >
    {children}
  </button>
);

const StepDots = ({ step, total }) => (
  <div className="mb-6 flex items-center gap-2" data-testid="recommendation-step-dots">
    {Array.from({ length: total }).map((_, i) => (
      <span
        key={i}
        className={`h-1.5 rounded-full transition-all ${i + 1 === step ? "w-10 bg-orange" : i + 1 < step ? "w-6 bg-navy/40" : "w-6 bg-navy/10"}`}
      />
    ))}
    <span className="ml-2 text-xs font-medium uppercase tracking-wider text-navy/60">
      Step {step} of {total}
    </span>
  </div>
);

// ---------- Initial state ----------
const initial = {
  fullName: "", mobile: "", email: "",
  qualification: "", passingYear: "", currentCourse: "",
  technicalBackground: "", existingSkills: "",
  careerGoal: "", preferredDuration: "", preferredMode: "",
  budget: "", interests: [],
  consent: false,
};

const TOTAL_STEPS = 4;

export default function CourseRecommendation() {
  useSeo(
    "AI Course Recommendation",
    "Get a personalised ASAI program recommendation — free, rule-based and tailored to your goals, background and budget."
  );

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null); // null | array

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInterest = (tag) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(tag)
        ? f.interests.filter((t) => t !== tag)
        : [...f.interests, tag],
    }));
  };

  // ---------- Per-step validation ----------
  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (!form.fullName.trim()) e.fullName = "Please enter your full name.";
      if (!isValidIndianMobile(form.mobile)) e.mobile = "Enter a valid 10-digit Indian mobile number.";
      if (!isValidEmail(form.email)) e.email = "Enter a valid email address.";
      if (!form.qualification) e.qualification = "Please select your qualification.";
    }
    if (step === 2) {
      if (!form.technicalBackground) e.technicalBackground = "Please select your technical level.";
    }
    if (step === 3) {
      if (!form.careerGoal) e.careerGoal = "Please select a career goal.";
      if (!form.preferredDuration) e.preferredDuration = "Please pick a preferred duration.";
      if (!form.budget) e.budget = "Please pick a budget range.";
    }
    if (step === 4) {
      if (!form.consent) e.consent = "You must accept the consent to save your recommendation.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(TOTAL_STEPS, s + 1)); };
  const back = () => setStep((s) => Math.max(1, s - 1));

  // ---------- Compute recommendations + submit ----------
  const compute = useMemo(() => (profile) => recommendPrograms(profile, 3), []);

  const onSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const recs = compute(form);
      const payload = {
        fullName: form.fullName,
        mobile: form.mobile,
        email: form.email,
        qualification: form.qualification,
        passingYear: form.passingYear,
        currentCourse: form.currentCourse,
        technicalBackground: form.technicalBackground,
        existingSkills: form.existingSkills,
        careerGoal: form.careerGoal,
        preferredDuration: form.preferredDuration,
        preferredMode: form.preferredMode,
        budget: form.budget,
        interests: form.interests.join(", "),
        recommendedSlugs: recs.map((r) => r.program.slug),
        consent: true,
      };
      await submitCourseRecommendation(payload);
      setResults(recs);
      toast.success("Here are your personalised recommendations.");
      // scroll to top of results
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.message || "Something went wrong. Please try again.");
      if (err?.errors) setErrors(err.errors);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Reset ----------
  const restart = () => {
    setForm(initial);
    setStep(1);
    setErrors({});
    setResults(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="page-course-recommendation">
      <PageHeader
        overline="Course Recommendation"
        titleLines={["Find the right ASAI", "program for you"]}
        description="Answer a few questions and our rule-based recommender suggests programs that match your goals, background and budget. Free, private and no login required."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="container-asai max-w-4xl">
          {results === null ? (
            <div className="rounded-3xl border border-navy/10 bg-white p-6 shadow-[0_30px_60px_-40px_rgba(6,15,34,0.25)] md:p-10">
              <div className="mb-8 flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-orange/10 text-orange">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="font-heading text-2xl text-navy">Quick recommendation form</h2>
                  <p className="text-sm text-navy/60">Takes ~1 minute · Your details stay private.</p>
                </div>
              </div>

              <StepDots step={step} total={TOTAL_STEPS} />

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.25 }}
                >
                  {step === 1 && (
                    <div className="grid gap-5 md:grid-cols-2" data-testid="recommendation-step-1">
                      <FormInput
                        label="Full Name" id="cr-fullName" testId="cr-fullName"
                        value={form.fullName} onChange={(e) => set("fullName", e.target.value)}
                        placeholder="Your full name" error={errors.fullName}
                      />
                      <FormInput
                        label="Mobile (India)" id="cr-mobile" testId="cr-mobile"
                        value={form.mobile} onChange={(e) => set("mobile", e.target.value)}
                        placeholder="10-digit mobile" error={errors.mobile}
                      />
                      <FormInput
                        label="Email" id="cr-email" testId="cr-email" type="email"
                        value={form.email} onChange={(e) => set("email", e.target.value)}
                        placeholder="you@example.com" error={errors.email}
                      />
                      <Select
                        id="cr-qualification" label="Highest Qualification" testId="cr-qualification"
                        value={form.qualification} onChange={(v) => set("qualification", v)}
                        options={QUALIFICATIONS} error={errors.qualification}
                      />
                      <FormInput
                        label="Passing Year (Optional)" id="cr-passingYear" testId="cr-passingYear"
                        value={form.passingYear} onChange={(e) => set("passingYear", e.target.value)}
                        placeholder="e.g. 2026"
                      />
                      <FormInput
                        label="Current Course / Stream (Optional)" id="cr-currentCourse" testId="cr-currentCourse"
                        value={form.currentCourse} onChange={(e) => set("currentCourse", e.target.value)}
                        placeholder="e.g. B.Tech CSE"
                      />
                    </div>
                  )}

                  {step === 2 && (
                    <div className="grid gap-6" data-testid="recommendation-step-2">
                      <div>
                        <label className="mb-3 block text-xs font-semibold uppercase tracking-wider text-navy/70">
                          Your technical background
                        </label>
                        <div className="flex flex-wrap gap-2" data-testid="cr-tech-levels">
                          {TECH_LEVELS.map((lvl) => (
                            <Chip
                              key={lvl} active={form.technicalBackground === lvl}
                              onClick={() => set("technicalBackground", lvl)}
                              testId={`cr-tech-${lvl.toLowerCase()}`}
                            >
                              {lvl}
                            </Chip>
                          ))}
                        </div>
                        {errors.technicalBackground && (
                          <span className="mt-2 block text-xs text-destructive">{errors.technicalBackground}</span>
                        )}
                      </div>
                      <FormInput
                        as="textarea" rows={3}
                        label="Existing Skills (Optional)"
                        id="cr-existingSkills" testId="cr-existingSkills"
                        value={form.existingSkills} onChange={(e) => set("existingSkills", e.target.value)}
                        placeholder="e.g. Python basics, Excel, Java, SQL…"
                      />
                    </div>
                  )}

                  {step === 3 && (
                    <div className="grid gap-6" data-testid="recommendation-step-3">
                      <Select
                        id="cr-goal" label="Career Goal" testId="cr-goal"
                        value={form.careerGoal} onChange={(v) => set("careerGoal", v)}
                        options={CAREER_GOALS} error={errors.careerGoal}
                      />
                      <div className="grid gap-5 md:grid-cols-3">
                        <Select
                          id="cr-duration" label="Preferred Duration" testId="cr-duration"
                          value={form.preferredDuration} onChange={(v) => set("preferredDuration", v)}
                          options={DURATIONS} error={errors.preferredDuration}
                        />
                        <Select
                          id="cr-mode" label="Preferred Mode" testId="cr-mode"
                          value={form.preferredMode} onChange={(v) => set("preferredMode", v)}
                          options={MODES}
                        />
                        <Select
                          id="cr-budget" label="Budget" testId="cr-budget"
                          value={form.budget} onChange={(v) => set("budget", v)}
                          options={BUDGETS} error={errors.budget}
                        />
                      </div>
                      <div>
                        <label className="mb-3 block text-xs font-semibold uppercase tracking-wider text-navy/70">
                          Interests (pick as many as you like)
                        </label>
                        <div className="flex flex-wrap gap-2" data-testid="cr-interests">
                          {INTEREST_TAGS.map((tag) => (
                            <Chip
                              key={tag} active={form.interests.includes(tag)}
                              onClick={() => toggleInterest(tag)}
                              testId={`cr-interest-${tag.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            >
                              {tag}
                            </Chip>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="grid gap-5" data-testid="recommendation-step-4">
                      <div className="rounded-2xl bg-lightgrey p-6">
                        <h3 className="mb-3 font-heading text-lg text-navy">Review your details</h3>
                        <dl className="grid gap-3 text-sm md:grid-cols-2">
                          {[
                            ["Name", form.fullName],
                            ["Mobile", form.mobile],
                            ["Email", form.email],
                            ["Qualification", form.qualification],
                            ["Technical Level", form.technicalBackground],
                            ["Career Goal", form.careerGoal],
                            ["Duration", form.preferredDuration],
                            ["Mode", form.preferredMode || "No preference"],
                            ["Budget", form.budget],
                            ["Interests", form.interests.join(", ") || "—"],
                          ].map(([k, v]) => (
                            <div key={k} className="flex flex-col gap-0.5">
                              <dt className="text-xs uppercase tracking-wider text-navy/50">{k}</dt>
                              <dd className="text-navy/85">{v || "—"}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>

                      <label className="flex items-start gap-3 text-sm text-navy/75" data-testid="cr-consent-label">
                        <input
                          type="checkbox" checked={form.consent}
                          onChange={(e) => set("consent", e.target.checked)}
                          data-testid="cr-consent"
                          className="mt-1 h-4 w-4 rounded border-navy/25"
                        />
                        <span>
                          I consent to ASAI storing my details to share personalised recommendations and
                          contact me about relevant programs. See our{" "}
                          <Link to="/privacy-policy" className="text-royal underline">Privacy Policy</Link>.
                        </span>
                      </label>
                      {errors.consent && (
                        <span className="text-xs text-destructive">{errors.consent}</span>
                      )}

                      <div className="flex items-start gap-3 rounded-xl border border-navy/10 bg-white p-4 text-xs text-navy/60">
                        <Info className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
                        <span>
                          Advisory disclaimer: This recommendation is generated by a rule-based engine using
                          the details you provide and is meant purely as guidance. Final program selection is
                          your choice. ASAI does not guarantee placement.
                        </span>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Nav buttons */}
              <div className="mt-10 flex items-center justify-between">
                <button
                  type="button" onClick={back} disabled={step === 1}
                  data-testid="cr-back"
                  className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-5 py-2.5 text-sm font-medium text-navy/70 transition-colors hover:border-orange hover:text-orange disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" /> Back
                </button>
                {step < TOTAL_STEPS ? (
                  <PrimaryButton onClick={next} data-testid="cr-next">
                    Continue <ChevronRight className="ml-1 h-4 w-4" />
                  </PrimaryButton>
                ) : (
                  <PrimaryButton onClick={onSubmit} disabled={submitting} data-testid="cr-submit">
                    {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating…</>) : (<>Show my recommendations <Sparkles className="ml-2 h-4 w-4" /></>)}
                  </PrimaryButton>
                )}
              </div>
            </div>
          ) : (
            <ResultsView results={results} form={form} onRestart={restart} />
          )}
        </div>
      </section>
    </div>
  );
}

// ---------- Results ----------
function ResultsView({ results, form, onRestart }) {
  const top = results[0];
  const others = results.slice(1);
  return (
    <div data-testid="cr-results">
      <div className="mb-8 rounded-3xl bg-navy p-8 text-white md:p-10">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs uppercase tracking-wider text-white/80">
          <Sparkles className="h-3.5 w-3.5 text-orange" /> Your top match
        </div>
        <h2 className="font-heading text-3xl leading-tight md:text-4xl" data-testid="cr-top-program-name">
          {top.program.name}
        </h2>
        <p className="mt-3 max-w-3xl text-sm text-white/70 md:text-base">{top.program.tagline || top.program.homeSummary}</p>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <ResultMeta icon={Clock} label="Duration" value={top.program.duration} onDark />
          <ResultMeta icon={MonitorSmartphone} label="Mode" value={top.program.mode} onDark />
          <ResultMeta icon={IndianRupee} label="Fee" value={top.program.fee} onDark />
        </div>
        <ul className="mt-6 space-y-2 text-sm text-white/85">
          {top.reasons.map((r) => (
            <li key={r} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-orange" /> {r}</li>
          ))}
        </ul>

        {top.program.outcomes?.length > 0 && (
          <div className="mt-6 rounded-2xl bg-white/5 p-5">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-white/60">
              Learning outcomes
            </div>
            <ul className="grid gap-2 text-sm text-white/80 md:grid-cols-2" data-testid="cr-top-outcomes">
              {top.program.outcomes.map((o) => (
                <li key={o} className="flex items-start gap-2">
                  <Award className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> {o}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to={detailPath(top.program)}
            className="inline-flex items-center gap-2 rounded-full bg-orange px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-orange/90"
            data-testid="cr-top-view-details"
          >
            View program details <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link
            to={`/apply?program=${encodeURIComponent(top.program.slug)}`}
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            data-testid="cr-top-apply"
          >
            Apply now
          </Link>
          <a
            href={waLinkFor(top.program.name)} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            data-testid="cr-top-counseling"
          >
            Request counseling
          </a>
        </div>
      </div>

      {others.length > 0 && (
        <div className="mb-8">
          <h3 className="mb-4 font-heading text-2xl text-navy">Other strong options for you</h3>
          <div className="grid gap-5 md:grid-cols-2">
            {others.map(({ program, reasons }) => (
              <div
                key={program.slug}
                data-testid={`cr-other-${program.slug}`}
                className="rounded-2xl border border-navy/10 bg-white p-6 transition-all hover:-translate-y-0.5 hover:shadow-[0_30px_60px_-40px_rgba(6,15,34,0.35)]"
              >
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-royal">
                  {program.category === "internship" ? "Internship" : "Program"}
                </div>
                <h4 className="font-heading text-xl text-navy">{program.name}</h4>
                <p className="mt-2 line-clamp-2 text-sm text-navy/60">{program.tagline || program.homeSummary}</p>
                <div className="mt-4 grid gap-2 text-sm text-navy/75">
                  <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-orange" /> {program.duration}</div>
                  <div className="flex items-center gap-2"><IndianRupee className="h-4 w-4 text-orange" /> {program.fee}</div>
                  <div className="flex items-center gap-2"><GraduationCap className="h-4 w-4 text-orange" /> {program.mode}</div>
                </div>
                {reasons.length > 0 && (
                  <ul className="mt-4 space-y-1 text-xs text-navy/60">
                    {reasons.slice(0, 2).map((r) => (
                      <li key={r} className="flex items-start gap-1.5">
                        <Award className="mt-0.5 h-3.5 w-3.5 text-orange" /> {r}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-5 flex flex-wrap gap-2">
                  <Link
                    to={detailPath(program)}
                    className="inline-flex items-center gap-1 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-navy/85"
                    data-testid={`cr-other-view-${program.slug}`}
                  >
                    View details <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                  <Link
                    to={`/apply?program=${encodeURIComponent(program.slug)}`}
                    className="inline-flex items-center gap-1 rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy transition-colors hover:border-orange hover:text-orange"
                  >
                    Apply
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col items-start gap-3 rounded-2xl border border-navy/10 bg-lightgrey p-6 text-sm text-navy/70 md:flex-row md:items-center md:justify-between">
        <p>
          Want to try a different profile, {form.fullName.split(" ")[0]}? Recompute your recommendation any time.
        </p>
        <SecondaryButton onClick={onRestart} data-testid="cr-restart">Try again</SecondaryButton>
      </div>

      <p className="mt-6 text-xs text-navy/50">
        Note: This is guidance from our rule-based engine using the details you provided. Please verify
        program specifics with our counselor. ASAI provides placement assistance — not guaranteed placement.
      </p>
    </div>
  );
}

const ResultMeta = ({ icon: I, label, value, onDark }) => (
  <div className={`flex items-center gap-3 rounded-xl px-4 py-3 ${onDark ? "bg-white/5" : "bg-lightgrey"}`}>
    <I className={`h-4 w-4 ${onDark ? "text-orange" : "text-royal"}`} />
    <div className="min-w-0">
      <div className={`text-[10px] uppercase tracking-wider ${onDark ? "text-white/50" : "text-navy/50"}`}>{label}</div>
      <div className={`truncate text-sm font-semibold ${onDark ? "text-white" : "text-navy"}`}>{value}</div>
    </div>
  </div>
);
